import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Badge } from "../ui/badge";
import { Button } from "../ui/button";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from "recharts";
import { ShieldAlert, Lock, MonitorSmartphone, KeyRound, Siren, Users } from "lucide-react";

export const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—");

const Stat = ({ icon: Icon, label, value, tone = "text-slate-900", testId }) => (
  <Card data-testid={testId}>
    <CardContent className="p-4 flex items-center gap-3">
      <div className="w-9 h-9 rounded-sm bg-slate-100 flex items-center justify-center"><Icon size={18} className="text-cyan-700" /></div>
      <div>
        <p className={`text-xl font-semibold ${tone}`}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </CardContent>
  </Card>
);

function AlertsCard({ alerts, onAck }) {
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">Spike alerts</CardTitle></CardHeader>
      <CardContent className="space-y-2" data-testid="security-alerts-list">
        {!alerts.length && <p className="text-sm text-slate-500">No alerts. Checks run every 15 minutes.</p>}
        {alerts.map((a) => (
          <div key={a.id} className={`p-3 border rounded-sm flex items-start justify-between gap-3 ${a.acknowledged ? "bg-white" : "bg-red-50 border-red-200"}`}>
            <div>
              <p className="text-sm font-medium">{a.title}</p>
              <p className="text-xs text-slate-600 mt-0.5">{a.details}</p>
              <p className="text-[11px] text-slate-400 mt-1">{fmtDT(a.created_at)}{a.acknowledged ? ` · acknowledged by ${a.acknowledged_by}` : ""}</p>
            </div>
            {!a.acknowledged && <Button size="sm" variant="outline" className="h-7 rounded-sm" onClick={() => onAck(a.id)} data-testid={`ack-alert-${a.id.slice(0, 6)}`}>Acknowledge</Button>}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const SimpleList = ({ title, rows, empty, render, testId }) => (
  <Card>
    <CardHeader><CardTitle className="text-base">{title}</CardTitle></CardHeader>
    <CardContent className="space-y-1.5" data-testid={testId}>
      {!rows.length ? <p className="text-sm text-slate-500">{empty}</p> : rows.map(render)}
    </CardContent>
  </Card>
);

export function ActivityTab({ data, onAck }) {
  const t = data.totals;
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
        <Stat icon={ShieldAlert} label="Failed sign-ins (7 days)" value={t.failed_7d} testId="stat-failed" />
        <Stat icon={Lock} label="Lockouts (7 days)" value={t.lockouts_7d} testId="stat-lockouts" />
        <Stat icon={MonitorSmartphone} label="New-device sign-ins (7 days)" value={t.new_devices_7d} testId="stat-new-devices" />
        <Stat icon={Lock} label="Accounts locked now" value={t.locked_now} tone={t.locked_now ? "text-red-600" : "text-slate-900"} testId="stat-locked-now" />
        <Stat icon={KeyRound} label="Active users with MFA" value={`${t.mfa_users} / ${t.active_users}`} testId="stat-mfa" />
        <Stat icon={Siren} label="Open incidents" value={t.open_incidents} tone={t.open_incidents ? "text-amber-600" : "text-slate-900"} testId="stat-open-incidents" />
      </div>
      <div className="grid grid-cols-1 xl:grid-cols-[2fr_1fr] gap-6">
        <Card>
          <CardHeader><CardTitle className="text-base">Last 7 days</CardTitle></CardHeader>
          <CardContent data-testid="security-daily-chart">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={data.daily.map((d) => ({ ...d, day: d.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="day" fontSize={11} /><YAxis allowDecimals={false} fontSize={11} /><Tooltip /><Legend />
                <Bar dataKey="failed" name="Failed sign-ins" fill="#f59e0b" />
                <Bar dataKey="lockouts" name="Lockouts" fill="#dc2626" />
                <Bar dataKey="new_devices" name="New devices" fill="#0e7490" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
        <AlertsCard alerts={data.alerts} onAck={onAck} />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <SimpleList title="Admins without MFA" rows={data.admins_without_mfa} empty="Every admin has MFA switched on." testId="admins-without-mfa"
          render={(u) => <div key={u.email} className="text-sm flex justify-between"><span>{u.name || u.email}</span><Badge variant="outline" className="text-xs rounded-sm">{u.role}</Badge></div>} />
        <SimpleList title="Locked accounts" rows={data.locked_accounts} empty="No accounts are locked." testId="locked-accounts"
          render={(l) => <div key={l.email} className="text-sm flex justify-between"><span>{l.email}</span><span className="text-xs text-slate-500">until {fmtDT(l.locked_until)}</span></div>} />
        <SimpleList title="By organisation (7 days)" rows={data.by_org} empty="No failed sign-ins." testId="security-by-org"
          render={(o) => <div key={o.org_name} className="text-sm flex justify-between"><span className="flex items-center gap-1.5"><Users size={13} />{o.org_name}</span><span className="text-xs text-slate-500">{o.failed} failed · {o.lockouts} locked</span></div>} />
      </div>
      <Card>
        <CardHeader><CardTitle className="text-base">Recent security events</CardTitle></CardHeader>
        <CardContent className="divide-y" data-testid="security-recent-events">
          {data.recent_events.map((e, i) => (
            <div key={`${e.timestamp}-${i}`} className="py-2 flex items-start gap-3 text-sm">
              <Badge className="text-xs rounded-sm bg-slate-100 text-slate-700 shrink-0">{e.action.replace(/_/g, " ")}</Badge>
              <div className="flex-1 min-w-0"><p className="truncate">{e.user_email} — {e.details}</p><p className="text-xs text-slate-400">{e.org_name}</p></div>
              <span className="text-xs text-slate-400 whitespace-nowrap">{fmtDT(e.timestamp)}</span>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
